import { useEffect, useState } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, { locales, isRtlLocale } from '@/i18n';
import { posthog } from '@/lib/posthog';
import { isChunkLoadError, reloadForFailedChunk } from '@/lib/chunkReload';

/**
 * What a customer sees when a page crashes.
 *
 * Until this existed, they saw React Router's built-in fallback: the words
 * "Unexpected Application Error!" above a raw `<pre>` of the JavaScript stack,
 * minified vendor frames and all. A founder screenshot from production shows a
 * customer staring at eleven lines of `vendor-BjHAMOwc.js:32:25720` after
 * clicking to create a video.
 *
 * That screen is not a missing nicety, it is a leak: it publishes the internal
 * shape of the app to anyone who can make it throw, it is untranslatable (RR
 * renders it in English under an otherwise Hebrew UI), and it offers no way out
 * except the browser's own reload button.
 *
 * `AppErrorBoundary` already existed and did NOT prevent this. It wraps
 * `<RouterProvider>`, and React Router installs its own boundary on every route
 * — the inner one wins, so the outer class component was never reached for any
 * error thrown inside a page. A boundary in the wrong place looks exactly like
 * a boundary that works, right up until a customer sends you a photograph.
 *
 * Registered as `errorElement` on every top-level route.
 */

/** Did something outside React rewrite the page's text? */
function pageWasMachineTranslated(): boolean {
  try {
    const c = document.documentElement.className || '';
    // Google Translate stamps the root element and swaps text nodes for <font>
    // wrappers. React then tries to remove a child that is no longer where it
    // left it, which throws NotFoundError from insertBefore/removeChild — the
    // exact crash reported from production.
    return /\btranslated-(ltr|rtl)\b/.test(c) || !!document.querySelector('font[_msttexthash], .goog-te-banner-frame');
  } catch { return false; }
}

/**
 * The locale from the URL, since nothing else has set it.
 *
 * `LocaleLayout` is what normally calls `changeLanguage` — and this component
 * REPLACES it, because the errorElement lives on that same route. So a Hebrew
 * user reaching /he/... through a crash would be met by an English apology, on
 * the one screen where being understood matters most. The path segment is the
 * app's own source of truth for language, so read it directly.
 */
function localeFromPath(): string | null {
  try {
    const seg = window.location.pathname.split('/')[1];
    return seg && (locales as readonly string[]).includes(seg) ? seg : null;
  } catch { return null; }
}

/**
 * A page of the app could not be fetched because a newer build was deployed
 * while this tab was open. That is not a crash: say so calmly and reload once
 * (bounded by lib/chunkReload). Only if the reload cannot happen (already tried,
 * or uploads are running) does the usual screen appear.
 */
function UpdatingScreen({ error }: { error: any }) {
  const { t } = useTranslation();
  const [reloading, setReloading] = useState<boolean | null>(null);
  useEffect(() => { setReloading(reloadForFailedChunk()); }, []);
  if (reloading === false) return <ErrorScreen notFound={false} error={error} />;
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] p-6">
      <div className="max-w-md w-full bg-[#16161e] border border-white/8 rounded-xl p-6 text-center">
        <h1 className="text-lg font-semibold text-white mb-2">{t('errors.updatingTitle', 'Updating Soltik')}</h1>
        <p className="text-sm text-[#a0a0b0]">{t('errors.updatingBody', 'A newer version is available. Loading it now…')}</p>
      </div>
    </div>
  );
}

export default function RouteErrorBoundary() {
  const error = useRouteError() as any;
  if (isChunkLoadError(error)) return <UpdatingScreen error={error} />;

  // Report it. The old screen showed the stack to the customer and sent it
  // nowhere; this is the other way round, which is the right way round.
  try {
    posthog.captureException(error instanceof Error ? error : new Error(String(error?.message || error)), {
      source: 'route_error_boundary',
      // Path only. A crash on a page carrying a token in its query string
      // (magic-link verify, media tokens) must not ship that token to analytics.
      url: window.location.origin + window.location.pathname,
      machineTranslated: pageWasMachineTranslated(),
      status: isRouteErrorResponse(error) ? error.status : undefined,
    });
  } catch { /* never let reporting replace the fallback with another crash */ }

  return <ErrorScreen notFound={isRouteErrorResponse(error) && error.status === 404} error={error} />;
}

/**
 * The same screen, minus the crash.
 *
 * The catch-all route renders this directly for a URL that carries a locale and
 * matches nothing. Nothing threw there, so there is no error to report and no
 * stack to show — but the visitor still needs to be told the page is not there,
 * in their own language, with a way out.
 */
export function NotFound() {
  return <ErrorScreen notFound />;
}

/**
 * Dev-only: what the debug block prints.
 *
 * A route error is not always an Error. A 404 raised by a loader arrives as a
 * route error RESPONSE, which has no stack and no message and stringifies to the
 * famously unhelpful "[object Object]" — so say what it actually is.
 */
function devDetail(error: any): string {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText} — ${window.location.pathname}`;
  }
  return String(error?.stack || error?.message || error);
}

function ErrorScreen({ notFound, error }: { notFound: boolean; error?: any }) {
  const { t } = useTranslation();
  const translated = pageWasMachineTranslated();

  useEffect(() => {
    const loc = localeFromPath();
    if (loc && i18n.language !== loc) i18n.changeLanguage(loc).catch(() => { /* keep English */ });
    if (loc) {
      document.documentElement.lang = loc;
      document.documentElement.dir = isRtlLocale(loc) ? 'rtl' : 'ltr';
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] p-6">
      <div className="max-w-md w-full bg-[#16161e] border border-white/8 rounded-xl p-6 text-center">
        <h1 className="text-lg font-semibold text-white mb-2">
          {notFound
            ? t('errors.notFoundTitle', 'Page not found')
            : t('errors.crashTitle', 'Something went wrong')}
        </h1>

        {translated ? (
          // Reloading a page the browser will simply translate again reproduces
          // the crash, so saying "reload" here would be advice that cannot work.
          <p className="text-sm text-[#a0a0b0] mb-5">
            {t('errors.translatedBody',
              "Your browser is translating this page automatically, which can break it. Soltik is already available in your language — turn off the browser translation and pick your language from the menu instead.")}
          </p>
        ) : (
          <p className="text-sm text-[#a0a0b0] mb-5">
            {notFound
              ? t('errors.notFoundBody', "That page does not exist, or it has moved.")
              : t('errors.crashBody', "We have been notified and are looking into it. Reloading usually fixes it.")}
          </p>
        )}

        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-iris-600 hover:bg-iris-500 text-[#0C0C12] text-sm font-medium"
          >{t('errors.reload', 'Reload')}</button>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="px-4 py-2 rounded-lg border border-white/12 text-[#e8e8f0] text-sm font-medium hover:bg-white/4"
          >{t('errors.goHome', 'Go home')}</button>
        </div>

        {/* The stack stays in the bundle for developers and stays OUT of the
            customer's way: dev builds only, never production. */}
        {import.meta.env.DEV && error != null && (
          <pre className="mt-5 text-left text-[11px] leading-relaxed text-[#7a7a8c] whitespace-pre-wrap max-h-64 overflow-auto">
            {devDetail(error)}
          </pre>
        )}
      </div>
    </div>
  );
}
